import {
  assertSessionGenerationCurrent, captureSessionGeneration,
  clearSession, getAccessToken, installSessionContextBoundary
} from "../api/client";
import { clearPlatformSession, getPlatformAccessToken } from "../platform/platform-client";

export const SESSION_EXPIRED_EVENT = "leylion-session-expired";

let installed = false;

function isApiRequest(input: RequestInfo | URL): boolean {
  const value = typeof input === "string"
    ? input
    : input instanceof URL
      ? input.toString()
      : input.url;

  try {
    const url = new URL(value, window.location.origin);
    return url.origin === window.location.origin && url.pathname.startsWith("/api/");
  } catch {
    return false;
  }
}

async function isInvalidSessionResponse(response: Response): Promise<boolean> {
  if (response.status !== 401) return false;

  try {
    const payload = await response.clone().json() as { title?: string; type?: string };
    return payload.title === "SESSION_INVALID"
      || payload.type?.endsWith("/SESSION_INVALID") === true;
  } catch {
    return false;
  }
}

function captureOperationsRead(input: RequestInfo | URL, init?: RequestInit) {
  try {
    const request = input instanceof Request ? input : null;
    const url = new URL(request ? request.url : String(input), window.location.origin);
    const method = (init?.method ?? request?.method ?? "GET").toUpperCase();
    if (url.origin !== window.location.origin
      || !["/api/v1/platform-console/operations", "/api/v1/platform-console/executive"].includes(url.pathname)
      || method !== "GET") return null;

    return {
      authorization: new Headers(init?.headers !== undefined ? init.headers : request?.headers).get("Authorization"),
      platformToken: getPlatformAccessToken(),
      accessToken: getAccessToken(),
      signal: init?.signal !== undefined ? init.signal : request?.signal
    };
  } catch {
    return null;
  }
}

function isCurrentOperationsRead(read: NonNullable<ReturnType<typeof captureOperationsRead>>): boolean {
  return !read.signal?.aborted
    && read.platformToken !== null
    && read.authorization === "Bearer " + read.platformToken
    && getPlatformAccessToken() === read.platformToken
    && getAccessToken() === read.accessToken;
}

function captureManagerPinRead(input: RequestInfo | URL, init?: RequestInit) {
  try {
    const request = input instanceof Request ? input : null;
    const url = new URL(request ? request.url : String(input), window.location.origin);
    const method = (init?.method ?? request?.method ?? "GET").toUpperCase();
    if (url.origin !== window.location.origin
      || url.pathname !== "/api/v1/onboarding/first-login/manager-pin"
      || method !== "GET") return null;

    return {
      authorization: new Headers(init?.headers !== undefined ? init.headers : request?.headers).get("Authorization"),
      accessToken: getAccessToken(),
      platformToken: getPlatformAccessToken(),
      signal: init?.signal !== undefined ? init.signal : request?.signal
    };
  } catch {
    return null;
  }
}

function isCurrentManagerPinRead(read: NonNullable<ReturnType<typeof captureManagerPinRead>>): boolean {
  return !read.signal?.aborted
    && read.accessToken !== null
    && read.authorization === "Bearer " + read.accessToken
    && getAccessToken() === read.accessToken
    && getPlatformAccessToken() === read.platformToken;
}

export function installSessionExpiryBoundary(): void {
  if (installed || typeof window === "undefined") return;
  installed = true;
  installSessionContextBoundary();

  const nativeFetch = window.fetch.bind(window);
  window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    const sessionGeneration = isApiRequest(input) ? captureSessionGeneration() : null;
    // Bind this protected read to its caller before awaiting the response.
    const operationsRead = captureOperationsRead(input, init);
    const managerPinRead = captureManagerPinRead(input, init);
    const response = await nativeFetch(input, init);
    if (sessionGeneration !== null) assertSessionGenerationCurrent(sessionGeneration);

    // JWT challenges can be empty; a late response must not clear a newer session.
    // PIN state checks also receive empty JWT challenges. Only the current
    // authenticated GET may recover the session; failures are never PIN setup.
    // Other API consumers retain their existing SESSION_INVALID contract.
    const shouldExpire = operationsRead
      ? response.status === 401 && isCurrentOperationsRead(operationsRead)
      : managerPinRead
        ? response.status === 401 && isCurrentManagerPinRead(managerPinRead)
        : isApiRequest(input) && await isInvalidSessionResponse(response);
    if (sessionGeneration !== null) assertSessionGenerationCurrent(sessionGeneration);
    if (shouldExpire) {
      clearPlatformSession();
      clearSession();
      window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT));
    }

    return response;
  };
}
